/**
 * interior_doors — THE TENANT'S DOOR in the visit (the interiors programme, slice G task 2, 2026-10-02). PURE.
 *
 * Slice R lets a unit; slice G made a unit a ROOM with a doorway. The server refuses every ENTRY of a let unit's room
 * but its tenant's (`server/src/lib/interior_door_ops.ts`); this file is what the visit DRAWS and COLLIDES so a body
 * meets a door where the server would refuse it:
 *
 *   · `unitDoorsOf(d)`     every doorway of every room that IS a rental unit — its aperture (from the partition's own
 *                          span), the wall's thickness and height, and which side of the wall the unit lies on;
 *   · `doorShut(...)`      is this door SHUT for this viewer? — the unit is let, to someone else, and both levers are
 *                          on. The tenant's own door, an unlet unit and either lever off are OPEN;
 *   · THE ONE-WAY RULE     (`doorRelation` + `doorPassNext`) a shut door's collider exists only for a body OUTSIDE the
 *                          room. A body INSIDE it — a visitor who stood there when the lease was signed, a guest — and
 *                          a body standing IN the doorway never have a box put on them or between them and the
 *                          corridor: they walk out, the leaf closes behind them, and they cannot come back.
 *                          NOBODY IS EVER SEALED IN (interiors.md laws 6–8, 22, 38). A latch, not a test: a body
 *                          pressed against the shut leaf from the corridor is "in the doorway" by a skin's width and
 *                          must stay shut out, so the doorway zone KEEPS the last answer;
 *   · `leafBoxes(...)`     the leaf itself, procedural: a slab between the jambs with two inset panels and a pull on
 *                          either face (shut), or the same slab slid aside into the wall with only its edge showing
 *                          (open) — a sliding door needs no swing room, so it can never cut a piece of furniture.
 *
 * The collider is THICK — the partition's own 0.4 m, the full height of the wall (the open header over a doorway is
 * drawn, never collided, so a leaf-high box could be jumped) — exactly like the wall it completes.
 */
import { FLOOR_H } from './building_interiors.js';
import { roomAt, type DescDoorway, type DescFloor, type DescPartition, type InteriorDescriptor } from './interior_descriptor.js';
import { floorDoorSafe } from './interior_rooms.js';

/** The capsule's radius (PhysicsModule `CAPSULE_RADIUS`; `verify:interior-rooms` pins the two equal). */
export const DOOR_BODY_R = 0.3;
/** A body this near the door's box (beyond its own radius) counts as standing IN the doorway (m). Wider than the
 *  character controller's skin, so a body pressed on the leaf is still "in the doorway" and the latch holds. */
export const DOOR_SKIN_M = 0.12;
/** The leaf: its thickness, the jamb it closes against (interior_wiring `partitionBoxesFor`'s frame), the head. */
export const LEAF_T = 0.07, LEAF_JAMB_M = 0.08, LEAF_HEAD_M = 2.15;
/** An open leaf shows this much of itself at the jamb it slid toward (m). */
export const LEAF_OPEN_SHOW_M = 0.1;

/** One doorway of a room that is a rental unit, building-local metres. */
export interface UnitDoor {
  /** The doorway's id. */
  id: string;
  unitId: string;
  /** The unit's room, and the room on the other side (the corridor). */
  roomId: string; other: string;
  floor: number;
  /** The aperture's centre on the wall's mid-line. */
  x: number; z: number;
  /** The wall's direction: 'x' = it runs along x (a body crosses it walking ±z). */
  axis: 'x' | 'z';
  /** The aperture's whole span along the wall (jamb to jamb, m), the wall's thickness and height (m). */
  span: number; thickness: number; height: number;
  /** +1 / −1: the unit lies on this side of the wall along the crossing axis. */
  inSign: 1 | -1;
  /** The floor's slab above the building's ground (m): 0 on the ground floor, `floor · FLOOR_H` above. */
  baseY: number;
  /** MAY IT EVER BE SHUT? (`interior_rooms.floorDoorSafe` — the server asks the same of its own plan.) */
  safe: boolean;
}

/** A descriptor floor's door safety: the rule's input cut exactly as `roomPlanFrom` cuts the server's (the link nodes'
 *  rooms by the nearest room within 1.5 m, else the floor's entry room). */
export function descFloorDoorSafe(f: Pick<DescFloor, 'rooms' | 'doorways' | 'routeNodes'>): boolean {
  const nodeRooms: string[] = [];
  for (const n of f.routeNodes) if (n.id === 'stair-foot' || n.id === 'landing' || n.id === 'lift-call') nodeRooms.push(roomAt(f, n.x, n.z, 1.5)?.id ?? f.rooms[0]?.id ?? '');
  return floorDoorSafe({ rooms: f.rooms, doorways: f.doorways, nodeRooms });
}

function apertureOf(w: DescPartition, way: DescDoorway): { x: number; z: number; span: number } | null {
  const alongX = Math.abs(w.x1 - w.x0) >= Math.abs(w.z1 - w.z0);
  let best: { x: number; z: number; span: number } | null = null, bestD = Infinity;
  for (const [a0, a1] of w.apertures) {
    const m = (a0 + a1) / 2;
    const x = alongX ? w.x0 + m : w.x0, z = alongX ? w.z0 : w.z0 + m;
    const dd = Math.hypot(x - way.x, z - way.z);
    if (dd < bestD) { bestD = dd; best = { x, z, span: a1 - a0 }; }
  }
  return best && bestD <= 0.75 ? best : null;
}

/** Every doorway of every unit room of a planned descriptor (a room below the rooms revision has none: nothing
 *  encloses a unit there, and the server's rule finds no unit room either). Deterministic order: floors, doorways. */
export function unitDoorsOf(d: Pick<InteriorDescriptor, 'floors'> | null): UnitDoor[] {
  const out: UnitDoor[] = [];
  if (!d) return out;
  for (const f of d.floors) {
    const unitOf = new Map<string, string>();
    for (const r of f.rooms) if (r.unitId) unitOf.set(r.id, r.unitId);
    if (unitOf.size === 0) continue;
    const baseY = f.floor === 0 ? 0 : f.floor * FLOOR_H;
    const safe = descFloorDoorSafe(f);
    for (const way of f.doorways) {
      for (const [roomId, other, sx, sz] of [[way.a, way.b, way.ax, way.az], [way.b, way.a, way.bx, way.bz]] as Array<[string, string, number, number]>) {
        const unitId = unitOf.get(roomId);
        if (!unitId) continue;
        const w = f.partitions.find((p) => p.id === way.partition);
        const ap = w ? apertureOf(w, way) : null;
        const across = way.axis === 'x' ? sz - way.z : sx - way.x;
        out.push({
          id: way.id, unitId, roomId, other, floor: f.floor, axis: way.axis,
          x: ap?.x ?? way.x, z: ap?.z ?? way.z, span: ap?.span ?? way.width + 2 * LEAF_JAMB_M,
          thickness: w?.thickness ?? 0.4, height: w?.height ?? FLOOR_H - 0.3,
          inSign: across >= 0 ? 1 : -1, baseY, safe,
        });
      }
    }
  }
  return out;
}

/** What the viewer needs to know about the leases of one building (`rental_client.letUnitsOf`). */
export interface DoorLease { unitId: string; tenantHex: string }
/** The levers as this client last read them (`platform_flag`, ABSENT = ON). */
export interface DoorLevers { rooms: boolean; rentals: boolean }

/** Who holds the unit ('' = nobody: an open room). */
export function doorTenant(door: Pick<UnitDoor, 'unitId'>, lets: ReadonlyArray<DoorLease>): string {
  for (const l of lets) if (l.unitId === door.unitId) return l.tenantHex;
  return '';
}
/** IS THE DOOR SHUT FOR THIS VIEWER? — exactly where the server refuses an entry: the unit is let, the viewer is not
 *  its tenant (the landlord is nobody special), and both levers are on. `meHex` '' (not connected) is nobody's tenant. */
export function doorShut(door: Pick<UnitDoor, 'unitId' | 'safe'>, lets: ReadonlyArray<DoorLease>, meHex: string, levers: DoorLevers): boolean {
  if (!levers.rooms || !levers.rentals || !door.safe) return false;
  const t = doorTenant(door, lets);
  return t !== '' && t !== meHex;
}

/** Where a body stands relative to one door. */
export type DoorRel = 'inside' | 'doorway' | 'outside';
export interface DoorBody { floor: number; lx: number; lz: number; roomId: string }
/** `doorway`: the body's circle (+ a skin) touches the door's box. Else `inside` when its room is the unit's. */
export function doorRelation(door: UnitDoor, body: DoorBody): DoorRel {
  if (body.floor !== door.floor) return 'outside';
  const along = door.axis === 'x' ? body.lx - door.x : body.lz - door.z;
  const across = door.axis === 'x' ? body.lz - door.z : body.lx - door.x;
  const reach = DOOR_BODY_R + DOOR_SKIN_M;
  if (Math.abs(along) <= door.span / 2 + reach && Math.abs(across) <= door.thickness / 2 + reach) return 'doorway';
  return body.roomId === door.roomId ? 'inside' : 'outside';
}
/**
 * THE ONE-WAY LATCH: may this body pass a SHUT door? Inside the room → yes (the way out is never blocked); outside →
 * no; in the doorway → whatever it was (`prev`), and on the FIRST look (`null`: the lease was just signed, the visit
 * just began) yes — a box is never put on a body.
 */
export function doorPassNext(prev: boolean | null, rel: DoorRel): boolean {
  if (rel === 'inside') return true;
  if (rel === 'outside') return false;
  return prev ?? true;
}
/** Does the door's box stand, this frame? (shut, and the latch does not pass the body) */
export const doorBlocks = (shut: boolean, pass: boolean): boolean => shut && !pass;

/** The door's THICK box (building-local, `y` the centre above the building's ground): the whole aperture, the wall's
 *  own thickness, the wall's full height. */
export function doorBox(door: UnitDoor): { x: number; y: number; z: number; w: number; h: number; d: number } {
  const alongX = door.axis === 'x';
  return { x: door.x, y: door.baseY + door.height / 2, z: door.z, w: alongX ? door.span : door.thickness, h: door.height, d: alongX ? door.thickness : door.span };
}

/** One drawn box of a leaf (building-local; `role` picks the look's material). THE LEAF'S LOOK (slice H, 2026-10-02 —
 *  the tenant's door handed on by slice G): the slab is the partition's DOOR material (the tradition's wood over a kick
 *  plate — it was the flat frame tone, so the shut leaf read as a thin frame), the inset panels the darker FRAME tone (they
 *  were the partition's panel material, whose cap line ran across the lower panel like the wall's dado), the pull its own
 *  PULL colour (the trim's cap — the hand's metal), and a THRESHOLD sill across the opening in both states. */
export interface LeafBox { x: number; y: number; z: number; w: number; h: number; d: number; role: 'panel' | 'frame' | 'door' | 'pull' }
/** The sill across a let room's doorway: low enough never to trip the capsule (the KCC's autostep takes 0.8 m). */
export const LEAF_SILL_H = 0.03;
/**
 * The leaf as boxes. SHUT: a slab between the jambs under the head, two inset panels and a pull on either face, the
 * sill. OPEN: the slab slid toward the jamb at −span/2 — `LEAF_OPEN_SHOW_M` of it left in the opening, its leading edge
 * picked out in the frame tone with the pull on it — and the sill.
 */
export function leafBoxes(door: UnitDoor, shut: boolean): LeafBox[] {
  const alongX = door.axis === 'x';
  const clear = Math.max(0.2, door.span - 2 * LEAF_JAMB_M), head = Math.min(LEAF_HEAD_M - LEAF_JAMB_M, door.height);
  const out: LeafBox[] = [];
  // (s = metres along the wall from the aperture's centre, t = metres across it toward +axis, y above the slab)
  const box = (s: number, y: number, t: number, ls: number, h: number, lt: number, role: LeafBox['role']): void => {
    out.push({ x: door.x + (alongX ? s : t), y: door.baseY + y, z: door.z + (alongX ? t : s), w: alongX ? ls : lt, h, d: alongX ? lt : ls, role });
  };
  const sill = (): void => box(0, LEAF_SILL_H / 2, 0, clear, LEAF_SILL_H, LEAF_T + 0.08, 'frame');
  if (!shut) {
    const e = -clear / 2 + LEAF_OPEN_SHOW_M; // the leading edge, at the jamb's side of the opening
    box(-clear / 2 + LEAF_OPEN_SHOW_M / 2, head / 2, 0, LEAF_OPEN_SHOW_M, head, LEAF_T, 'door');
    box(e - 0.0125, head / 2, 0, 0.025, head, LEAF_T + 0.012, 'frame');            // the edge, in the frame tone
    box(e - 0.03, head * 0.5, 0, 0.03, 0.34, LEAF_T + 0.08, 'pull');                // the pull, both faces
    sill();
    return out;
  }
  box(0, head / 2, 0, clear, head, LEAF_T, 'door');
  const pw = clear - 0.24, proud = LEAF_T / 2 + 0.006;
  for (const side of [-1, 1]) {
    box(0, head * 0.72, side * proud, pw, head * 0.36, 0.012, 'frame');   // the upper panel
    box(0, head * 0.29, side * proud, pw, head * 0.34, 0.012, 'frame');   // the lower panel (above the kick plate)
    box(clear / 2 - 0.09, head * 0.5, side * (LEAF_T / 2 + 0.02), 0.03, 0.34, 0.04, 'pull'); // the pull
  }
  sill();
  return out;
}
